import {
    useCallback,
    useSyncExternalStore,
    type CSSProperties,
    type FC,
    type ReactNode,
} from 'react'
import {
    useAgent,
    useCopilotKit,
    useCopilotChatConfiguration,
    useFrontendTool,
} from '@copilotkit/react-core/v2'
import { z } from 'zod'
import {
    cardStyle,
    FONT,
    TEXT,
    DIMMED,
    BRAND,
    BRAND_LIGHT,
} from './widgetStyles'
import {
    booleanEditToBlocks,
    type Block,
    type BadgeItem,
} from './renderMappers'
import { datasetIdToName } from '@/lib/datasetNames'
import type { AgentState } from '../types'

// ---- styles -----------------------------------------------------------------
const headingStyle: CSSProperties = {
    fontFamily: FONT,
    fontWeight: 600,
    fontSize: 'var(--mantine-font-size-lg)',
    lineHeight: 1.3,
    color: TEXT,
}
const textStyle: CSSProperties = {
    fontFamily: FONT,
    fontSize: 'var(--mantine-font-size-sm)',
    color: TEXT,
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap',
}
const pillBase: CSSProperties = {
    display: 'inline-block',
    fontFamily: FONT,
    fontSize: 'var(--mantine-font-size-xs)',
    fontWeight: 600,
    borderRadius: 'var(--mantine-radius-sm)',
    padding: '2px 8px',
    lineHeight: 1.4,
}
// `color` is a Mantine palette name (e.g. 'grape'); its light-variant CSS vars
// keep the pill theme-aware (and dark-mode safe). No color → the brand tint.
const pillStyle = (color?: string): CSSProperties => ({
    ...pillBase,
    color: color ? `var(--mantine-color-${color}-light-color)` : BRAND,
    background: color ? `var(--mantine-color-${color}-light)` : BRAND_LIGHT,
})

const normBadge = (b: BadgeItem): { label: string; color?: string } =>
    typeof b === 'string' ? { label: b } : b

function renderBlock(block: Block): ReactNode {
    switch (block.type) {
        case 'heading':
            return block.text ? (
                <div style={headingStyle}>{block.text}</div>
            ) : null
        case 'text':
            return block.text ? <div style={textStyle}>{block.text}</div> : null
        case 'badges': {
            const items = (block.items ?? [])
                .map(normBadge)
                .filter((b) => b.label)
            return items.length ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {items.map((b) => (
                        <span
                            key={`${b.label}:${b.color ?? ''}`}
                            style={pillStyle(b.color)}
                        >
                            {b.label}
                        </span>
                    ))}
                </div>
            ) : null
        }
        case 'table': {
            const rows = (block.rows ?? []).filter(
                (r) => r && (r.label || r.value)
            )
            return rows.length ? (
                <div
                    style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
                >
                    {rows.map((r) => (
                        <div
                            key={`${r.label ?? ''}:${r.value ?? ''}`}
                            style={{
                                display: 'flex',
                                gap: 8,
                                fontFamily: FONT,
                                fontSize: 'var(--mantine-font-size-sm)',
                                lineHeight: 1.4,
                            }}
                        >
                            {r.label && (
                                <span
                                    style={{
                                        color: DIMMED,
                                        fontWeight: 600,
                                        minWidth: 110,
                                        flexShrink: 0,
                                    }}
                                >
                                    {r.label}
                                </span>
                            )}
                            {r.value && (
                                <span
                                    style={{
                                        color: TEXT,
                                        flex: 1,
                                        minWidth: 0,
                                    }}
                                >
                                    {r.value}
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            ) : null
        }
        case 'list': {
            const items = (block.items ?? []).filter(Boolean)
            return items.length ? (
                <div
                    style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
                >
                    {items.map((it) => (
                        <div
                            key={it}
                            style={{
                                display: 'flex',
                                gap: 6,
                                fontFamily: FONT,
                                fontSize: 'var(--mantine-font-size-sm)',
                                color: TEXT,
                                lineHeight: 1.45,
                            }}
                        >
                            <span
                                style={{ color: DIMMED, flexShrink: 0 }}
                                aria-hidden
                            >
                                •
                            </span>
                            <span style={{ flex: 1, minWidth: 0 }}>{it}</span>
                        </div>
                    ))}
                </div>
            ) : null
        }
        default:
            return null
    }
}

// A content key (never the array index) so React is happy and oxlint's
// no-array-index-key stays satisfied.
const blockKey = (b: Block): string => {
    if (b.type === 'heading' || b.type === 'text')
        return `${b.type}:${b.text ?? ''}`
    if (b.type === 'badges')
        return `badges:${(b.items ?? []).map((x) => normBadge(x).label).join('|')}`
    if (b.type === 'table')
        return `table:${(b.rows ?? []).map((r) => r.label).join('|')}`
    return `list:${(b.items ?? []).join('|')}`
}

// Display-only chat widget: renders a `frontend_widget_render` call (or an
// auto-rendered side-effect call mapped via the helpers below) as a themed card
// of stacked blocks. It does NOT collect input — decisions go through the
// ChoicePrompt choice widget.
export const RenderBlocks: FC<{ args: Record<string, unknown> }> = ({
    args,
}) => {
    const raw = (args as { blocks?: unknown } | undefined)?.blocks
    // Tool-driven input: drop non-object items (null/undefined/strings) so
    // blockKey/renderBlock never throw on a malformed block.
    const blocks: Block[] = Array.isArray(raw)
        ? (raw.filter((b) => b && typeof b === 'object') as Block[])
        : []
    const rendered = blocks
        .map((b) => ({ key: blockKey(b), el: renderBlock(b) }))
        .filter((x) => x.el != null)

    if (!rendered.length) return null

    return (
        <div style={cardStyle}>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--mantine-spacing-sm)',
                }}
            >
                {rendered.map(({ key, el }) => (
                    <div key={key}>{el}</div>
                ))}
            </div>
        </div>
    )
}

// Card for the `edit_new_boolean_logic_audience` tool. That tool draws the card
// BEFORE the size exists (combine_segments_tool computes it afterward), and the
// chat message holding the card is wrapped in CopilotKit's React.memo — which
// re-renders only on its own message content, not on agent-state changes. So we
// subscribe explicitly via useSyncExternalStore: it re-renders this component
// whenever the snapshot (the size) changes, independent of memoized ancestors.
// We listen on run/state/message events (so the size lands at the latest by run
// end) and read it by segment_id from the editing audience (live, pre-cart) or
// the cart (post-cart) — both kept in sync server-side.
export const EditBooleanCard: FC<{ args: Record<string, unknown> }> = ({
    args,
}) => {
    const { copilotkit } = useCopilotKit()
    const agentId = useCopilotChatConfiguration()?.agentId
    const { agent } = useAgent(agentId ? { agentId } : undefined)
    const segmentId = args.segment_id as string

    const subscribe = useCallback(
        (onChange: () => void) => {
            const sub = copilotkit.subscribeToAgentWithOptions(
                agent,
                {
                    onStateChanged: onChange,
                    onMessagesChanged: onChange,
                    onRunFinalized: onChange,
                },
                { throttleMs: 0 }
            )
            return () => sub.unsubscribe()
        },
        [copilotkit, agent]
    )
    const getSize = useCallback((): number | undefined => {
        const state = agent.state as Partial<AgentState> | null
        const editing = state?.editingUserAttributeAudience
        const fromEditing =
            editing && editing.segment_id === segmentId
                ? editing.audience_size
                : undefined
        const fromCart = state?.audienceConfigurations?.find(
            (c) => c.segment_id === segmentId
        )?.audience_size
        // ?? not || so a legitimate editing size of 0 wins over the cart value
        // (0 means "not computed yet", which formatReach renders as nothing).
        return fromEditing ?? fromCart
    }, [agent, segmentId])

    const audienceSize = useSyncExternalStore(subscribe, getSize)

    return (
        <RenderBlocks
            args={booleanEditToBlocks(args, datasetIdToName, audienceSize)}
        />
    )
}

// Parameters the agent fills when calling `frontend_widget_render`. Mirrors the
// `RenderBlock` discriminated union + field docs that used to live on the
// backend `frontend_widget_render` tool.
const renderSchema = z.object({
    blocks: z
        .array(
            z.discriminatedUnion('type', [
                z.object({
                    type: z.literal('heading'),
                    text: z.string().describe('A short, bold heading.'),
                }),
                z.object({
                    type: z.literal('text'),
                    text: z.string().describe('A paragraph of text.'),
                }),
                z.object({
                    type: z.literal('badges'),
                    items: z
                        .array(z.string())
                        .describe(
                            'A row of short tag/pill labels (e.g. a dataset, a reach estimate like "~2.0M").'
                        ),
                }),
                z.object({
                    type: z.literal('table'),
                    rows: z
                        .array(
                            z.object({
                                label: z
                                    .string()
                                    .describe(
                                        'Short row label, e.g. "Brand" or "Reach".'
                                    ),
                                value: z
                                    .string()
                                    .describe('The row value — keep it short.'),
                            })
                        )
                        .describe(
                            'A key/value table — the clearest way to show structured facts.'
                        ),
                }),
                z.object({
                    type: z.literal('list'),
                    items: z
                        .array(z.string())
                        .describe('A simple bulleted list.'),
                }),
            ])
        )
        .describe(
            'Ordered list of content blocks (heading / text / badges / table / list), rendered top-to-bottom in a single card.'
        ),
})

// Registers `frontend_widget_render` as a CLIENT-SIDE (non-HITL) tool so the
// agent can render structured cards. Unlike ChoicePrompt's useHumanInTheLoop,
// it does not wait for the user: the handler returns immediately (which also
// resolves the proxied long-running tool call on the backend so the run
// continues) while `render` draws the blocks. Call once from a component inside
// the CopilotKit provider tree (ThreadPage).
export function useRenderWidgetTool(): void {
    useFrontendTool(
        {
            name: 'frontend_widget_render',
            description: `Render structured information to the user as a clean card, instead of writing it out as a prose paragraph. The DEFAULT way to present any structured result — a segment's criteria, a recommendation, a summary, a comparison, key facts, or next steps.

Pass an ordered list of \`blocks\`; they render top-to-bottom inside one card. Mix and match as many as you need:
- {"type": "heading", "text": "..."} — a short bold heading
- {"type": "text", "text": "..."} — a paragraph
- {"type": "badges", "items": ["A", "B"]} — short tag pills (e.g. a dataset, a reach estimate like "~2.0M")
- {"type": "table", "rows": [{"label": "Brand", "value": "The Home Depot"}]} — key/value facts, clearest for criteria / attributes
- {"type": "list", "items": ["...", "..."]} — a bulleted list

It is DISPLAY-ONLY — it does not ask a question or wait for input (use \`ask_user_question\` for decisions). An empty list renders nothing, so when plain prose is enough, just write it instead of calling this.`,
            parameters: renderSchema,
            // Display-only: nothing to compute. Returning a resolved promise
            // immediately unblocks the backend's (long-running) proxied tool
            // call so the agent continues; `render` shows the card.
            handler: () => Promise.resolve('Rendered to the user.'),
            render: ({ args }) => <RenderBlocks args={args} />,
        },
        []
    )
}
